import React, { useEffect, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { BarChart3, Eye, Calendar, Clock } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    if (
      hostname.includes('webcontainer.io') ||
      hostname.includes('webcontainer-api.io')
    ) {
      return `${protocol}//${hostname.replace('--5173--', '--5002--')}/api`;
    }

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    return `${protocol}//${hostname}/api`;
  }

  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};

interface VisitByDate {
  date: string;
  count: number;
}

interface RecentVisit {
  id: string;
  path: string;
  visitedAt: string;
  ip?: string;
}

const VisitorAnalytics: React.FC = () => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [periodTotal, setPeriodTotal] = useState(0);
  const [visitsByDate, setVisitsByDate] = useState<VisitByDate[]>([]);
  const [recentVisits, setRecentVisits] = useState<RecentVisit[]>([]);
  const [days, setDays] = useState(30);

  const fetchVisits = async (rangeDays: number) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${getApiBaseUrl()}/analytics/visits?days=${rangeDays}&limit=200`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) throw new Error('Failed to load analytics');

      const data = await response.json();
      setTotal(data.total);
      setPeriodTotal(data.periodTotal);
      setVisitsByDate([...data.visitsByDate].reverse());
      setRecentVisits(data.recentVisits);
    } catch {
      setVisitsByDate([]);
      setRecentVisits([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisits(days);
  }, [days]);

  const formatDateTime = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });

  const cardClass =
    theme === 'dark'
      ? 'bg-gray-800 border-gray-700 text-white'
      : 'bg-white border-gray-200 text-gray-900';

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Site Visitors
          </h1>
          <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Landing page visit counts with date and time
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className={`rounded-lg border px-3 py-2 text-sm ${
            theme === 'dark'
              ? 'bg-gray-800 border-gray-700 text-white'
              : 'bg-white border-gray-300 text-gray-900'
          }`}
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <div className={`rounded-xl border p-5 ${cardClass}`}>
          <div className="flex items-center gap-3">
            <Eye className={`w-8 h-8 ${theme === 'dark' ? 'text-cyan-400' : 'text-blue-600'}`} />
            <div>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Visits ({days} days)
              </p>
              <p className="text-3xl font-bold">{loading ? '—' : periodTotal}</p>
            </div>
          </div>
        </div>
        <div className={`rounded-xl border p-5 ${cardClass}`}>
          <div className="flex items-center gap-3">
            <BarChart3 className={`w-8 h-8 ${theme === 'dark' ? 'text-cyan-400' : 'text-blue-600'}`} />
            <div>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                All-time visits
              </p>
              <p className="text-3xl font-bold">{loading ? '—' : total}</p>
            </div>
          </div>
        </div>
      </div>

      <div className={`rounded-xl border p-5 mb-8 ${cardClass}`}>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Visits by date
        </h2>
        {loading ? (
          <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Loading…</p>
        ) : visitsByDate.length === 0 ? (
          <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
            No visits recorded yet. Open the homepage in a new session to test.
          </p>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={visitsByDate}>
                <XAxis
                  dataKey="date"
                  tick={{ fill: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: 11 }}
                  tickFormatter={(v) => v.slice(5)}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme === 'dark' ? '#1f2937' : '#fff',
                    border: `1px solid ${theme === 'dark' ? '#374151' : '#e5e7eb'}`,
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="count" fill={theme === 'dark' ? '#22d3ee' : '#2563eb'} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className={`rounded-xl border overflow-hidden ${cardClass}`}>
        <div className={`px-5 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Recent visits
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className={theme === 'dark' ? 'bg-gray-900/50' : 'bg-gray-50'}>
              <tr>
                <th className="px-5 py-3 text-left font-medium">Date & time</th>
                <th className="px-5 py-3 text-left font-medium">Page</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={2} className="px-5 py-8 text-center text-gray-500">
                    Loading…
                  </td>
                </tr>
              ) : recentVisits.length === 0 ? (
                <tr>
                  <td colSpan={2} className="px-5 py-8 text-center text-gray-500">
                    No visits yet
                  </td>
                </tr>
              ) : (
                recentVisits.map((visit) => (
                  <tr
                    key={visit.id}
                    className={`border-t ${
                      theme === 'dark' ? 'border-gray-700' : 'border-gray-100'
                    }`}
                  >
                    <td className="px-5 py-3 whitespace-nowrap">
                      {formatDateTime(visit.visitedAt)}
                    </td>
                    <td className="px-5 py-3">{visit.path}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default VisitorAnalytics;
